import { PageBackground, PageShell, LoadingScreen, LoadingTitle } from "./styles";
import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { baseColors } from "../../themes/tokens";
import {
  IAddress,
  IPayment,
  IStore,
  ISale,
  ICity,
  IUser,
  ISaleForm,
  VoucherType,
} from "../../classes/interfaces";
import { toast } from "react-toastify";
import { DualRing } from "react-spinners-css";
import { cityToUrl } from "../../classes/strings";
import {
  getCities,
  getDistricts,
  calculateDistance,
  getPayments,
  validateVoucher,
} from "../../services/paymentsService";
import { postSale, buildSaleProducts } from "../../services/saleService";
import { useCart } from "../../hooks/useCart";
import AddressListModal from "./components/AddressListModal";
import DeleteAddressModal from "./components/DeleteAddressModal";
import NewAddressModal, { AddressFormValues } from "./components/NewAddressModal";
import MoneyPaymentModal from "./components/MoneyPaymentModal";
import IdentificationForm from "./components/IdentificationForm";
import DeliveryTypeForm from "./components/DeliveryTypeForm";
import DeliveryAddressForm from "./components/DeliveryAddressForm";
import VoucherForm from "./components/VoucherForm";
import PaymentSummary from "./components/PaymentSummary";
import LoadingOverlay from "../../components/ui/LoadingOverlay";
import {
  buildSavedAddress,
  generateAddressKey,
  getSavedStoreDistrict,
  getStoreDistrict,
  readSavedAddresses,
  writeSavedAddresses,
} from "./helpers/savedAddresses";

interface VoucherValidateRequest {
  suffix: string;
  voucherCode: string;
  orderTotal: number;
}

interface ValidateVoucherResult {
  voucher: { id: string; code: string; type: VoucherType };
  discount: number;
}

export default function Payments() {
  const navigate = useNavigate();
  const { suffix } = useParams<{ suffix: string }>();
  const { cart, setCart, clearCart } = useCart();
  const [state, setState] = React.useState({ status: "create" });
  const [payments, setPayments] = React.useState<IPayment[]>();
  const [cities, setCities] = React.useState<ICity[]>();
  const [cityOptions, setCityOptions] = React.useState<any[]>([]);
  const [districts, setDistricts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [voucher, setVoucher] = React.useState<ValidateVoucherResult | null>(
    null,
  );
  const [voucherError, setVoucherError] = React.useState("");
  const store: IStore = JSON.parse(
    sessionStorage.getItem("@menuchef/store") || "{}",
  );

  const [modalIsOpen, setIsOpen] = React.useState(false);
  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }

  // Formulário de endereço vazio (cadastro novo), na cidade da loja.
  const emptyAddressForm = (): AddressFormValues => ({
    zipCode: store.zipCode,
    address: "",
    number: "",
    district: "",
    districtId: null,
    cityId: store.city.id,
    city: store.city.name,
    complement: "",
    reference: "",
  });

  // Estado próprio do formulário de endereço (cadastro/edição), separado do
  // endereço selecionado para o pedido (formValues + localAddress).
  const [addressForm, setAddressForm] =
    useState<AddressFormValues>(emptyAddressForm);

  const resetAddressForm = () => {
    setAddressForm(emptyAddressForm());
    loadDistricts(store.city.id);
  };

  const [isAddress, setIsAddress] = React.useState(false);
  function openModalAddress() {
    resetAddressForm();
    getCities(suffix || "")
      .then((cities) => {
        setCities(cities);
        setIsAddress(true);
      })
      .catch(() => {
        setState({ status: "404" });
      });
  }
  function closeModalAddress() {
    setIsAddress(false);
  }

  const [isMoney, setIsMoney] = React.useState(false);
  function openModalMoney() {
    setIsMoney(true);
  }
  function closeModalMoney() {
    setIsMoney(false);
  }

  const [isDeleteAddress, setIsDeleteAddress] = React.useState(false);
  const [keyAddress, setKeyAddress] = React.useState("");
  function deleteAddress(key: string) {
    setKeyAddress(key);
    setIsDeleteAddress(true);
  }
  function closeDeleteAddress() {
    setIsDeleteAddress(false);
  }

  function loadDistricts(cityId: number) {
    setLoading(true);
    setDistricts([]);
    getDistricts(cityId, store.id)
      .then((items) => {
        const aux: any[] = [];
        items.forEach((item) => {
          aux.push({
            value: item.id,
            label: item.name,
            tax: item.tax,
          });
        });
        setDistricts(aux);
        setLoading(false);
      })
      .catch(() => {
        setState({ status: "404" });
        setLoading(false);
      });
  }

  // Endereços salvos no navegador (localStorage "__addss", compartilhado por
  // todas as lojas). A leitura migra o formato antigo (districtId único) para o
  // bairro por loja e nunca derruba a tela com JSON inválido.
  let addresses: IAddress[] = readSavedAddresses(localStorage, store);

  let user: IUser = {
    name: "",
    phone: "",
  };
  const localUser = localStorage.getItem("@menuchef/user");
  if (localUser !== null) {
    user = JSON.parse(localUser);
  }

  const [formValues, setFormValues] = useState<ISaleForm>({
    taxPayerIdentificationNumber: "",
    name: user.name,
    phone: user.phone,
    zipCode: store.zipCode,
    address: "",
    number: "",
    district: "",
    cityId: store.city.id,
    city: store.city.name,
    complement: "",
    reference: "",
    deliveryType: store.hasDelivery ? "delivery" : "pickUp",
    changeFor: "",
    deliveryFee: 0.0,
    voucherType: null,
    voucherValue: null,
  });

  const [localAddress, setLocalAddress] = useState<IAddress>();

  // Chave do endereço salvo que está sendo editado (null = cadastro novo).
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // A loja cobra por bairro quando tem districts (ativos) cadastrados. Os
  // endereços salvos ficam no navegador do cliente (localStorage "__addss",
  // compartilhado por TODAS as lojas MenuChef), com o bairro escolhido em cada
  // loja (districtByStore[store.id]). O endereço "precisa de bairro" nesta loja
  // quando ainda não tem bairro dela ou quando o bairro gravado não está mais
  // entre os ativos (loja recriada, bairro apagado/desativado). Mandar um id
  // que não é desta loja ao backend cobrava taxa 0 ou a taxa de outra loja.
  const storeDistricts = store.districts || [];
  const storeUsesDistrict = storeDistricts.length > 0;
  const isStoreDistrict = (districtId?: number | null) =>
    districtId != null && storeDistricts.some((d) => d.id === districtId);
  const isAddressOutdated = (address: IAddress) =>
    storeUsesDistrict && getStoreDistrict(address, store) === null;

  // O backend recusa (406 "district not found") bairro que não é desta loja,
  // está inativo ou não existe.
  const isDistrictRejected = (error: any) =>
    error?.response?.status === 406 ||
    error?.response?.data?.data === "district not found";

  // Seleciona um endereço salvo: pede a taxa ao backend e só então marca o
  // endereço como escolhido (endereço na tela, campos do pedido e taxa). Se o
  // cálculo falhar, a seleção e a taxa anteriores ficam como estavam — antes o
  // endereço ficava selecionado com a taxa padrão da loja (0 em loja por
  // bairro) e o pedido podia ser finalizado assim.
  const applyAddress = (address: IAddress) => {
    // Só o bairro desta loja vai ao backend (o de outras lojas nunca). Loja sem
    // bairro calcula por distância e manda null. O id gravado aqui já passou
    // pelo selectAddress (ativo na loja) ou acabou de ser escolhido na lista da
    // cidade; o backend ainda confere (406 "district not found").
    const storeDistrict = storeUsesDistrict
      ? getSavedStoreDistrict(address, store.id)
      : null;
    const districtId = storeDistrict?.id ?? null;
    const districtName = storeDistrict?.name || address.district;
    const location = `${address.address}, ${address.number} - ${districtName}, ${address.city} - ${store.city.state}, ${address.zipCode}, Brasil`;
    setLoading(true);
    calculateDistance(store.id, cityToUrl(location), districtId)
      .then((result) => {
        setLoading(false);
        setLocalAddress({ ...address, district: districtName });
        setFormValues((previousState) => ({
          ...previousState,
          address: address.address,
          number: address.number,
          district: districtName,
          districtId: districtId,
          complement: address.complement,
          reference: address.reference,
          zipCode: address.zipCode,
          cityId: address.cityId,
          city: address.city,
          deliveryFee: result.delivery_fee,
        }));
        closeModal();
      })
      .catch((error) => {
        setLoading(false);
        if (storeUsesDistrict && isDistrictRejected(error)) {
          toast(
            "O bairro deste endereço não é atendido pela loja. Escolha o bairro novamente.",
            { type: "error", autoClose: 3500 },
          );
          openEditAddress(address, { clearDistrict: true });
          return;
        }
        toast(
          "Não foi possível calcular a taxa de entrega deste endereço. Tente novamente.",
          { type: "error", autoClose: 3000 },
        );
      });
  };

  // Abre o modal de endereço pré-preenchido para editar um endereço salvo. Não
  // mexe no endereço selecionado: ele só muda quando o endereço editado for
  // salvo e a taxa calculada. Em loja por bairro, sem um bairro ativo desta
  // loja gravado no endereço, o bairro fica vazio (o nome salvo aparece só
  // como dica) e o cliente precisa escolher o bairro na lista para salvar.
  const openEditAddress = (
    address: IAddress,
    options: { clearDistrict?: boolean } = {},
  ) => {
    const storeDistrict = options.clearDistrict
      ? null
      : getStoreDistrict(address, store);
    setEditingKey(address.key);
    setAddressForm({
      address: address.address,
      number: address.number,
      district: storeDistrict?.name || address.district,
      districtId: storeDistrict?.id ?? null,
      complement: address.complement,
      reference: address.reference,
      zipCode: address.zipCode,
      cityId: address.cityId,
      city: address.city,
    });
    closeModal();
    getCities(suffix || "")
      .then((cities) => {
        setCities(cities);
        loadDistricts(address.cityId);
        setIsAddress(true);
      })
      .catch(() => {
        setState({ status: "404" });
      });
  };

  // Seleciona um endereço salvo. Se ele estiver desatualizado para a regra de
  // cobrança atual, em vez de cobrar a taxa antiga em silêncio, abre a edição
  // forçando o cliente a reescolher o bairro (dropdown) e recalcular.
  const selectAddress = (address: IAddress) => {
    if (isAddressOutdated(address)) {
      toast("Confirme o bairro para atualizar a taxa de entrega deste endereço.", {
        type: "info",
        autoClose: 3500,
      });
      openEditAddress(address);
      return;
    }
    applyAddress(address);
  };

  const orderTotal =
    cart.total +
    (formValues.voucherType === VoucherType.Delivery ||
    (store.freeShippingFrom > 0 && cart.total >= store.freeShippingFrom)
      ? 0
      : formValues.deliveryFee) -
    (formValues.voucherValue || 0);

  const loadPayments = () => {
    if (!payments || payments.length <= 0) {
      getPayments(suffix || "")
        .then((data) => {
          setPayments(data);
        })
        .catch((error) => {
          console.log(error);
          setState({ status: "404" });
        });
    }
  };

  const handleChangeVoucher = useCallback(() => {
    setVoucherError("");
    setVoucher(null);
    const data: VoucherValidateRequest = {
      suffix: suffix || "",
      voucherCode: formValues.voucherCode || "",
      orderTotal: cart.total,
    };
    validateVoucher(data)
      .then((result) => {
        setVoucher(result);
        setFormValues((previousState: any) => ({
          ...previousState,
          voucherType: result.voucher.type,
          voucherValue: result.discount,
          voucherId: result.voucher.id,
        }));
      })
      .catch((error) => {
        console.log(error);
        setVoucherError(error.response.data.data.error);
      });
  }, [formValues.voucherCode, cart.total, setVoucher, setVoucherError, suffix]);

  useEffect(() => {
    loadPayments();
    loadDistricts(store.city.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount; loadPayments/loadDistricts/store.city.id are not stable references and adding them would re-run this on every render
  }, []);

  useEffect(() => {
    const options: any[] = [];
    if (cities && cities !== null && cities.length > 0) {
      cities.forEach((city) => {
        options.push({
          value: city.id,
          label: city.name,
        });
      });
    }
    setCityOptions(options);
  }, [cities]);

  if (!payments || payments.length <= 0) {
    return (
      <LoadingScreen>
        <DualRing color={baseColors.teal} />
        <LoadingTitle>Carregando...</LoadingTitle>
      </LoadingScreen>
    );
  }

  return (
    <>
      <PageBackground>
        <PageShell>
          <LoadingOverlay show={loading} />

        <AddressListModal
          isOpen={modalIsOpen}
          onClose={closeModal}
          // A lista mostra o nome do bairro desta loja quando o endereço já tem
          // um; senão, o último nome escolhido (em qualquer loja).
          addresses={addresses.map((address) => ({
            ...address,
            district: getStoreDistrict(address, store)?.name || address.district,
          }))}
          onSelectAddress={selectAddress}
          onEditAddress={openEditAddress}
          onRemoveAddress={(key) => deleteAddress(key)}
          onAddNewAddress={() => {
            closeModal();
            openModalAddress();
          }}
        />

        <DeleteAddressModal
          isOpen={isDeleteAddress}
          onClose={closeDeleteAddress}
          onConfirm={() => {
            const removedSelected = localAddress?.key === keyAddress;
            addresses = addresses.filter((address) => address.key !== keyAddress);
            writeSavedAddresses(localStorage, addresses);
            closeDeleteAddress();
            // Só desfaz a seleção se o endereço apagado era o selecionado.
            if (removedSelected) {
              setLocalAddress(undefined);
              setFormValues((previousState) => ({
                ...previousState,
                ...emptyAddressForm(),
                deliveryFee: 0,
              }));
            }
            setState({ status: "remove address" });
          }}
        />

        <NewAddressModal
          isOpen={isAddress}
          editing={editingKey !== null}
          onClose={() => {
            setEditingKey(null);
            closeModalAddress();
          }}
          formValues={addressForm}
          setFormValues={setAddressForm}
          cities={cities}
          cityOptions={cityOptions}
          districts={districts}
          onCityChange={(cityId, label) => {
            setAddressForm((previousState) => ({
              ...previousState,
              cityId: cityId,
              city: label,
              districtId: null,
              district: "",
            }));
            loadDistricts(cityId);
          }}
          onDistrictChange={(districtId, label) => {
            // A taxa não muda aqui: só quando o endereço for salvo e o backend
            // confirmar a taxa do bairro (applyAddress).
            setAddressForm((previousState) => ({
              ...previousState,
              districtId: districtId,
              district: label,
            }));
          }}
          onSubmit={() => {
            if (addressForm.zipCode.trim() === "") {
              toast("É necessário informar o CEP!", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            if (!addressForm.cityId || addressForm.cityId === null) {
              toast("É necessário escolher uma cidade!", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            if (storeUsesDistrict || districts.length > 0) {
              // O bairro precisa ser um dos bairros da loja listados para a
              // cidade — um id velho guardado no endereço não vale.
              if (
                !districts.some(
                  (district) => district.value === addressForm.districtId,
                )
              ) {
                toast("É necessário escolher um bairro!", {
                  type: "error",
                  autoClose: 2000,
                });
                return;
              }
            } else {
              if (addressForm.district.trim() === "") {
                toast("É necessário informar o Endereço!", {
                  type: "error",
                  autoClose: 2000,
                });
                return;
              }
            }
            if (addressForm.address.trim() === "") {
              toast("É necessário informar o Endereço!", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            if (addressForm.number.trim() === "") {
              toast("É necessário informar o Número do Endereço!", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            if (editingKey !== null) {
              // Edição de endereço salvo: atualiza no lugar (preserva a key) e
              // re-seleciona para recalcular a taxa já com o bairro correto. O
              // bairro escolhido vale só para esta loja; o das outras lojas
              // fica como estava (ver buildSavedAddress).
              const updated = buildSavedAddress(
                addressForm,
                store.id,
                editingKey,
                addresses.find((a) => a.key === editingKey),
              );
              addresses = addresses.map((a) =>
                a.key === editingKey ? updated : a,
              );
              writeSavedAddresses(localStorage, addresses);
              setEditingKey(null);
              closeModalAddress();
              setState({ status: "edit address" });
              applyAddress(updated);
              return;
            }

            // Chave única: `length + 1` repetia a chave de outro endereço
            // depois de apagar um (editar/remover mexia nos dois).
            const newAddress = buildSavedAddress(
              addressForm,
              store.id,
              generateAddressKey(addresses.map((a) => a.key)),
            );
            addresses = [...addresses, newAddress];
            writeSavedAddresses(localStorage, addresses);
            closeModalAddress();
            openModal();
            setState({ status: "new address" });
            setAddressForm(emptyAddressForm());
          }}
        />

        <MoneyPaymentModal
          isOpen={isMoney}
          onRequestClose={closeModalMoney}
          onCancel={closeModalMoney}
          formValues={formValues}
          setFormValues={setFormValues}
          onSemTroco={() => {
            if (!cart.payments) {
              cart.payments = [];
            }
            let obj = null;
            cart.payments.forEach((pay) => {
              if (pay.money) {
                obj = pay;
              }
            });
            if (obj !== null) {
              const index = cart.payments.indexOf(obj);
              if (index > -1) {
                cart.payments.splice(index, 1);
              }
            } else {
              payments.forEach((pay) => {
                if (pay.money) {
                  if (formValues.changeFor !== "") {
                    pay.changeFor = 0;
                  }
                  cart.payments.push(pay);
                }
              });
            }
            formValues.changeFor = "";
            setCart(cart);
            setState({ status: "payment money" });
            closeModalMoney();
          }}
          onConfirm={() => {
            if (!cart.payments) {
              cart.payments = [];
            }
            let obj = null;
            cart.payments.forEach((pay) => {
              if (pay.money) {
                obj = pay;
              }
            });
            if (obj !== null) {
              const index = cart.payments.indexOf(obj);
              if (index > -1) {
                cart.payments.splice(index, 1);
                formValues.changeFor = "";
              }
            } else {
              let containsError = false;
              payments.forEach((pay) => {
                if (pay.money && !containsError) {
                  if (formValues.changeFor !== "") {
                    pay.changeFor = Number.parseFloat(formValues.changeFor);
                    if (pay.changeFor < orderTotal) {
                      toast(
                        "O valor do troco não pode ser menor que o total a pagar!",
                        { type: "error", autoClose: 2000 },
                      );
                      pay.changeFor = 0;
                      pay.value = 0;
                      formValues.changeFor = "";
                      containsError = true;
                    }
                  }
                  if (!containsError) {
                    cart.payments.push(pay);
                  }
                }
              });
            }

            setCart(cart);
            setState({ status: "payment money" });
            closeModalMoney();
          }}
        />

        <IdentificationForm formValues={formValues} setFormValues={setFormValues} />

        <DeliveryTypeForm
          hasDelivery={store.hasDelivery}
          hasPickup={store.hasPickup}
          deliveryType={formValues.deliveryType}
          setFormValues={setFormValues}
        />

        {formValues.deliveryType === "delivery" && (
          <DeliveryAddressForm
            localAddress={localAddress}
            onOpenAddressModal={() => openModal()}
          />
        )}

        {store.isVoucher && (
          <VoucherForm
            voucherCode={formValues.voucherCode || ""}
            setFormValues={setFormValues}
            onApply={handleChangeVoucher}
            voucherError={voucherError}
            voucher={voucher}
          />
        )}

        <PaymentSummary
          store={store}
          cart={cart}
          formValues={formValues}
          orderTotal={orderTotal}
          voucher={voucher}
          paymentMethods={payments}
          isSending={state.status === "enviando"}
          // See PaymentSummary.tsx's prop comments -- these four handlers are
          // intentionally asymmetric (row vs. checkbox), not redundant. Do not unify.
          onSelectMoneyPayment={() => {
            let obj = null;
            cart.payments.forEach((pay) => {
              if (pay.money) {
                obj = pay;
              }
            });
            if (obj !== null) {
              const index = cart.payments.indexOf(obj);
              if (index > -1) {
                cart.payments.splice(0, cart.payments.length);
                formValues.changeFor = "";
              }
            } else {
              cart.payments.splice(0, cart.payments.length);
              formValues.changeFor = "";
              openModalMoney();
            }
            setCart(cart);
            // "payment dinheiro" replaces the original "payment " + payment.name --
            // this handler was lifted out of the payments.map() closure that used to
            // supply `payment`, so the literal name is hardcoded. state.status DOES
            // feed rendering elsewhere (isSending={state.status === "enviando"} drives
            // PaymentSummary's spinner), but this specific literal never equals
            // "enviando", so substituting it here has no rendering effect.
            setState({ status: "payment dinheiro" });
          }}
          onSelectNonMoneyPayment={(payment) => {
            if (!cart.payments) {
              cart.payments = [];
            }
            let obj = null;
            cart.payments.forEach((pay) => {
              if (pay.id === payment.id) {
                obj = pay;
              }
            });
            if (obj !== null) {
              const index = cart.payments.indexOf(obj);
              if (index > -1) {
                cart.payments.splice(0, cart.payments.length);
                formValues.changeFor = "";
              }
            } else {
              cart.payments.splice(0, cart.payments.length);
              formValues.changeFor = "";
              cart.payments.push(payment);
            }
            setCart(cart);
            setState({ status: "payment " + payment.name });
          }}
          onAddMoreItems={() => navigate(`/store/${suffix}`)}
          onFinalizeOrder={() => {
            user.name = formValues.name;
            user.phone = formValues.phone;

            if (user.name === "") {
              toast("É necessário informar o campo Nome.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            if (user.phone === "") {
              toast("É necessário informar o campo Telefone.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }

            localStorage.setItem("@menuchef/user", JSON.stringify(user));

            const sale: ISale = {
              store: {
                id: store.id,
              },
              tax:
                formValues.deliveryType === "delivery"
                  ? formValues.voucherType === VoucherType.Delivery ||
                    (store.freeShippingFrom > 0 &&
                      cart.total >= store.freeShippingFrom)
                    ? 0
                    : formValues.deliveryFee
                  : 0,
              taxPayerIdentificationNumber: (
                formValues.taxPayerIdentificationNumber || ""
              ).trim(),
              deliveryName: (formValues.name || "").trim(),
              deliveryType: formValues.deliveryType,
              deliveryAddress: (formValues.address || "").trim(),
              deliveryNumber: (formValues.number || "").trim(),
              deliveryDistrict: (formValues.district || "").trim(),
              deliveryComplement: (formValues.complement || "").trim(),
              deliveryReference: (formValues.reference || "").trim(),
              deliveryCity: {
                id: formValues.cityId,
              },
              deliveryZipCode: (formValues.zipCode || "").trim(),
              phone: (formValues.phone || "").trim(),
              note: "",
              products: [],
              payments: [],
              voucher: formValues.voucherId
                ? { id: formValues.voucherId || "" }
                : null,
              voucherType: formValues.voucherType,
              voucherValue: formValues.voucherValue,
            };

            sale.products = buildSaleProducts(cart.products);

            let valuePaymentMoney = 0;
            cart.payments.forEach((payment) => {
              if (payment.money) {
                valuePaymentMoney = payment.value;
              }
            });

            if (cart.payments.length > 1) {
              let payable = cart.total + sale.tax - (sale.voucherValue || 0);
              if (valuePaymentMoney > 0) {
                payable -= valuePaymentMoney;
              }
              if (payable <= 0) {
                toast(
                  "Suas formas de pagamentos excedem o valor total do pedido. Por favor, selecione apenas as que forem ser usadas.",
                  {
                    type: "error",
                    autoClose: 2000,
                  },
                );
                return;
              }
              cart.payments.forEach((payment) => {
                if (!payment.money) {
                  payment.value = payable / (cart.payments.length - 1);
                }
              });
            }

            cart.payments.forEach((payment) => {
              sale.payments.push({
                payment: {
                  id: payment.id,
                },
                value:
                  payment.value ||
                  cart.total + sale.tax - (sale.voucherValue || 0),
                changeFor: payment.changeFor,
              });
            });

            // validate
            if (sale.deliveryName === "") {
              toast("É necessário preencher o nome.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }

            if (sale.phone === "") {
              toast("É necessário preencher o telefone.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }

            if (sale.deliveryType === "delivery") {
              if (sale.deliveryAddress === "" && sale.deliveryDistrict === "") {
                toast("É necessário escolher um endereço.", {
                  type: "error",
                  autoClose: 2000,
                });
                return;
              }

              if (!formValues.cityId || formValues.cityId === null) {
                toast("O endereço selecionado precisa uma cidade!", {
                  type: "error",
                  autoClose: 2000,
                });
                return;
              }

              // Rede de segurança: loja que cobra por bairro só fecha pedido
              // de entrega com um bairro desta loja — sem isso a taxa sai 0 ou
              // a de outra loja. Frete grátis/cupom de entrega continuam
              // zerando a taxa: a checagem é do bairro, não do valor.
              if (
                storeUsesDistrict &&
                !isStoreDistrict(formValues.districtId) &&
                !districts.some(
                  (district) => district.value === formValues.districtId,
                )
              ) {
                toast(
                  "Escolha o bairro do endereço de entrega para calcular a taxa.",
                  { type: "error", autoClose: 3000 },
                );
                return;
              }
            }

            if (sale.products.length <= 0) {
              toast("Nenhum produto encontrado no carrinho.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }

            if (sale.payments.length <= 0) {
              toast("É necessário selecionar no mínimo uma forma de pagamento.", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }

            setState({ status: "enviando" });
            postSale(sale)
              .then((data) => {
                if (data === undefined || data.code === undefined) {
                  toast("Erro inesperado. Tente novamente mais tarde!", {
                    type: "error",
                    autoClose: 2000,
                  });
                  return;
                }
                if (data.code !== 200) {
                  if (data.status === "store closed") {
                    toast("Desculpe, a loja está fechada no momento.", {
                      type: "error",
                      autoClose: 2000,
                    });
                  }
                  return;
                }
                const saleId = data.data.sale.id;
                clearCart();
                toast(
                  "Seu pedido está com o estabelecimento, em instantes será produzido.",
                  {
                    type: "success",
                    autoClose: 2000,
                    onClose: () => navigate(`/terminate/${saleId}`),
                  },
                );
              })
              .catch((error) => {
                toast("Erro no servidor. Tente novamente mais tarde!", {
                  type: "error",
                  autoClose: 2000,
                });
              });
          }}
          />
        </PageShell>
      </PageBackground>
    </>
  );
}
